/* eslint-disable no-mixed-operators -- prettier clashes with the linter */
import type { TableCellProps } from '@mui/material';
import {
  Box,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow
} from '@mui/material';
import type { FC, MouseEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { NO_ORDERS_FOUND_MESSAGE, PAGE_CONTENT_MAX_WIDTH_LG, ROWS_PER_PAGE } from '~/constants';
import type { OrdersTableHeadCellType } from '~/types/OrdersTableData';
import type { OrdersTableProps } from './OrdersTable.types';
import { OrdersTableActions } from './OrdersTableActions';
import { OrdersTableHead } from './OrdersTableHead';
import { OrdersTableRow } from './OrdersTableRow';
import { OrdersTableToolbar } from './OrdersTableToolbar';

const headCells: OrdersTableHeadCellType[] = [
  { id: 'product', label: 'Product Name' },
  { id: 'sku', label: 'Sku No.' },
  { id: 'quantity', label: 'Qty' },
  { id: 'orderDate', label: 'Order Date' },
  { id: 'pharmacy', label: 'Pharmacy' },
  { id: 'address', label: 'Address' },
  { id: 'contact', label: 'Contact' },
  { id: 'approval', label: 'Approval ID' },
  { id: 'doctor', label: 'Dr. Initials' },
  { id: 'status', label: 'Status' },
  { id: 'dispatch', label: 'Dispatched' }
];
// TODO: this is a hack to keep the columns consistent and avoid type errors with sorting. Should find a better way to handle this
headCells[headCells.length - 1].visible = false;

export const OrdersTable: FC<OrdersTableProps> = ({
  data,
  isLoading,
  page,
  setPage,
  totalItems,
  status,
  setStatus,
  searchTerm,
  setSearchTerm,
  setDebouncedSearchTerm,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  handleApproveItems,
  approveLoading,
  handleRejectItems,
  rejectLoading,
  handleExport,
  isExportLoading
}) => {
  // Show the dispatch column if the data is B2C data
  if (data?.type === 'b2c') {
    headCells[headCells.length - 1].visible = true;
  } else {
    headCells[headCells.length - 1].visible = false;
  }

  const [selected, setSelected] = useState<string[]>([]);
  const maxPage = Math.max(0, Math.ceil(totalItems / ROWS_PER_PAGE) - 1);

  // Debounce the search term to avoid making too many re-renders
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, setDebouncedSearchTerm]);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const visibleColumns = headCells.filter((cell) => cell?.visible !== false);

  /**
   * Handles request to select all rows
   * @param event - event that triggered the request
   */
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.checked) {
      return setSelected([]);
    }

    const newSelected = rows.filter((row) => row.status === 'Awaiting Approval').map((row) => row.id);
    setSelected(newSelected);
  };

  /**
   * Handles click on a row to select it, or deselect it if it is already selected
   * @param id - the id of the row to select
   */
  const handleClick = (event: MouseEvent<HTMLTableRowElement>, id: string) => {
    const target = event.target as HTMLElement;
    if (target.closest('a')) {
      return;
    }

    setSelected((prevSelected) =>
      prevSelected.includes(id) ? prevSelected.filter((selectedId) => selectedId !== id) : [...prevSelected, id]
    );
  };

  /**
   * Handles changing the page of the table
   * @param newPage - the new page to navigate to
   */
  const handleChangePage = (newPage: number) => {
    if (!totalItems) {
      return;
    }

    // Don't allow navigating to a page that doesn't exist
    if (newPage < 0 || newPage * ROWS_PER_PAGE >= totalItems) {
      return;
    }

    setPage(newPage);
  };

  // Ensure the page is not out of bounds when the number of rows changes (e.g. after filtering)
  useEffect(() => {
    // Reset the page to the first page if it is out of bounds
    if (!isLoading && page > maxPage) {
      setPage(0);
    }
  }, [rows.length, page, setPage, totalItems, maxPage, isLoading]);

  const { emptyRows } = useMemo(() => {
    // Avoid a layout jump when reaching the last page with empty rows
    const calculatedEmptyRows = page > 0 ? Math.max(0, (1 + page) * ROWS_PER_PAGE - totalItems) : 0;

    return { emptyRows: calculatedEmptyRows };
  }, [totalItems, page]);

  // Get the rows that are selectable so that our select functionality works as expected
  const selectableRows = useMemo(() => rows.filter((row) => row.status === 'Awaiting Approval'), [rows]);

  const tableCellSx: TableCellProps['sx'] = {
    px: { xs: 1, lg: 2 },
    py: { xs: 2, lg: 1 },
    verticalAlign: 'top',
    bgcolor: 'background.paper',
    width: { lg: 200 }
  };

  return (
    <Box data-testid="orders-table" py={{ xs: 3.5, md: 5 }}>
      <Box maxWidth={PAGE_CONTENT_MAX_WIDTH_LG} mx="auto" px={{ xs: 3, lg: 6 }}>
        <Stack spacing={{ xs: 4, md: 5 }}>
          <OrdersTableToolbar
            status={status}
            setStatus={setStatus}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            handleExport={handleExport}
            isExportLoading={isExportLoading}
            type={data?.type ?? 'b2b'}
          />

          <TableContainer sx={{ height: '70dvh', overflow: 'auto' }}>
            <Table stickyHeader={true} sx={{ minWidth: '100%' }} aria-labelledby="Orders" size="small" cellSpacing={2}>
              <OrdersTableHead
                headCells={headCells}
                selectedCount={selected.length}
                rowCount={selectableRows.length}
                onSelectAllClick={handleSelectAllClick}
                isCheckboxDisabled={selectableRows.length === 0}
                isLoading={isLoading}
              />

              <TableBody>
                {rows.length > 0 && !isLoading ? (
                  rows.map((row, index) => {
                    const isItemSelected = selected.includes(row.id);

                    return (
                      <OrdersTableRow
                        key={row.id}
                        row={row}
                        index={index}
                        isItemSelected={isItemSelected}
                        handleClick={handleClick}
                        type={data?.type ?? 'b2b'}
                      />
                    );
                  })
                ) : isLoading ? (
                  // Show skeleton rows while loading
                  Array.from({ length: ROWS_PER_PAGE }).map((_, index) => (
                    <TableRow
                      key={`skeleton-row-${index}`}
                      data-testid="orders-table-row"
                      sx={{
                        height: 100,
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderLeft: '3px solid',
                        '&:nth-of-type(odd) td': { backgroundColor: 'grey.50' },
                        '&:nth-of-type(even) td': { backgroundColor: 'background.paper' }
                      }}
                    >
                      <TableCell
                        colSpan={visibleColumns.length + 1}
                        align="center"
                        sx={{
                          ...tableCellSx,
                          px: 0,
                          py: 0
                        }}
                      >
                        <Skeleton variant="rectangular" height={100} animation="wave" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow data-testid="orders-table-row">
                    {/* Plus 1 to account for checkbox column */}
                    <TableCell colSpan={visibleColumns.length + 1} align="center" sx={tableCellSx}>
                      {NO_ORDERS_FOUND_MESSAGE}
                    </TableCell>
                  </TableRow>
                )}

                {emptyRows > 0 && (
                  <TableRow style={{ height: 100 * emptyRows }} data-testid="orders-table-row">
                    <TableCell colSpan={visibleColumns.length + 1} sx={tableCellSx} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>

        {/* Hide pagination in instance that filtering suddenly changes the number of rows (avoids MUI warning) */}
        {!!totalItems && page <= maxPage ? (
          <TablePagination
            component="div"
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            count={totalItems}
            page={page}
            onPageChange={(_, page) => handleChangePage(page)}
            sx={{ display: 'flex', justifyContent: 'center' }}
          />
        ) : null}

        <OrdersTableActions
          selected={selected}
          setSelected={setSelected}
          handleApproveItems={handleApproveItems}
          approveLoading={approveLoading}
          handleRejectItems={handleRejectItems}
          rejectLoading={rejectLoading}
          type={data?.type ?? 'b2b'}
        />
      </Box>
    </Box>
  );
};
